/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { getSectionDims } from '#utils/PRIVATE_visLayoutUtils';
import { getTextInfo, positionMultilineText } from '#PRIVATE_VisSVGText/utils/utils';
import { TextProps } from '#PRIVATE_VisSVGText/text.types';
import { AxisPosition } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';

const defaultTitleProps = {
  text: '',
  lines: [],
  lineProps: [],
  titleDim: 0,
  dims: {
    width: 0,
    height: 0,
    x: 0,
    y: 0
  },
  isTruncated: false,
  isRotated: false
};

/**
 * Returns the title props.
 * @returns
 */
export function getTitleProps(
  title: string | undefined,
  titleStyle: Record<string, any>,
  maxHeight: number,
  availSpace: Dimension,
  position: AxisPosition,
  isRtl: boolean,
  getTextDimensions?: GetTextDimensionsType
): TextProps & { titleDim: number } {
  if (!title || !getTextDimensions) {
    return defaultTitleProps;
  }
  const isHoriz = position === 'top' || position === 'bottom';
  const maxWidth = isHoriz ? availSpace.width : availSpace.height;
  const { lines, height, width, dims, lineHeight, isTruncated } = getTextInfo(
    title,
    titleStyle,
    maxWidth,
    maxHeight,
    true,
    1,
    getTextDimensions
  );

  if (height > maxHeight || width === 0 || height === 0) {
    // title does not fit or not rendered
    return defaultTitleProps;
  }
  const isRotated = position === 'left' || position === 'right';
  const lineProps = positionMultilineText(lines, dims, lineHeight, position, availSpace, isRtl);
  const textDim = getSectionDims(availSpace, position, width, height);
  return {
    text: title,
    dims: textDim,
    titleDim: height,
    isTruncated,
    lines: position === 'left' || position === 'top' ? lines : lines.reverse(),
    lineProps,
    isRotated
  };
}
